import {range} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {ActionMethod, ActionOrigin, MODBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  ComplexPolymerAttachmentStyle,
  ComplexPolymerBondConnectionSide,
  ComplexPolymerConnection,
  ComplexPolymerType,
  HELMPolymerType,
  NucleotideModificationType,
  OLIGO_CONJUGATE_BACKBONE_STRAND_REF,
  OLIGO_DUPLEX_FORWARD_STRAND_REF,
  OLIGO_DUPLEX_REVERSE_STRAND_REF,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {
  getDisplayResidueIndex,
  getGQLComplexPolymerType,
  getPolymerTypeFromRef,
  isOligoDuplexReverseStrand,
  isPolymerBackboneStrand,
} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {
  AttachmentPointGraphQL,
  ConnectionSideType,
  ModificationTypeToMonomerName,
  MoleculeAttachmentPointGraphQL,
  MoleculeAttachmentQueryResult,
  NucleotideAttachmentPointGraphQL,
  NucleotideAttachmentQueryResult,
  NucleotideSimplePolymer,
  PLACEHOLDER_CONJUGATE_SOURCE_POLYMER_REF,
  SimplePolymer,
  UpdateConnectionFunc,
  areAttachmentPointNodes,
  getConnectionSide,
  getPolymerByRef,
  getSelectedPolymerRef,
  preprocessAttachmentPointAvailabilityFromConjugateConnections,
  preprocessAttachmentPoints,
  shouldDisableAttachButton,
} from 'modbio/complex-polymers/tabs/ComplexPolymerAttachConjugateModalUtil';
import {
  ConjugateConnection,
  ConjugateConnectionMap,
} from 'modbio/complex-polymers/tabs/ComplexPolymerComponentsSummaryUtil';
import {getReverseAdjustedDisplayResidueIdx} from 'modbio/complex-polymers/tabs/ComplexPolymerMapUtil';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Section from 'ui-library/menus/Section';
import SectionHeader from 'ui-library/menus/SectionHeader';
import {omit} from 'util/omitKey';

import {
  MoleculeAttachmentForCreateQuery,
  MoleculeAttachmentForCreateQueryVariables,
} from '../create/__generated/MoleculeAttachmentForCreateQuery';
import {
  NucleotideAttachmentForCreateQuery,
  NucleotideAttachmentForCreateQueryVariables,
} from '../create/__generated/NucleotideAttachmentForCreateQuery';
import {
  MOLECULE_ATTACHMENT_FOR_CREATE_QUERY,
  NUCLEOTIDE_ATTACHMENT_FOR_CREATE_QUERY,
} from '../create/CreateComplexPolymerAttachConjugatePane';
import {
  MoleculeAttachmentModalQuery,
  MoleculeAttachmentModalQueryVariables,
} from './__generated/MoleculeAttachmentModalQuery';
import {
  NucleotideAttachmentModalQuery,
  NucleotideAttachmentModalQueryVariables,
} from './__generated/NucleotideAttachmentModalQuery';
import {
  MOLECULE_ATTACHMENT_MODAL_QUERY,
  NUCLEOTIDE_ATTACHMENT_MODAL_QUERY,
} from './ComplexPolymerAttachConjugateModal';

type MoleculeChemicalStructureImageGraphQL = ExtractByTypename<
  MoleculeAttachmentModalQuery['moleculeInComplexPolymer'],
  'MoleculeInComplexPolymerNode'
>['chemicalStructureImage'];
type SugarChemicalStructureImageGraphQL = ExtractByTypename<
  NucleotideAttachmentModalQuery['nucleotideInComplexPolymer'],
  'NucleotideInComplexPolymerNode'
>['sugarChemicalStructureImage'];
type BaseChemicalStructureImageGraphQL = ExtractByTypename<
  NucleotideAttachmentModalQuery['nucleotideInComplexPolymer'],
  'NucleotideInComplexPolymerNode'
>['baseChemicalStructureImage'];
type PhosphateChemicalStructureImageGraphQL = ExtractByTypename<
  NucleotideAttachmentModalQuery['nucleotideInComplexPolymer'],
  'NucleotideInComplexPolymerNode'
>['phosphateChemicalStructureImage'];

export type ChemicalStructureImageGraphQL =
  | MoleculeChemicalStructureImageGraphQL
  | SugarChemicalStructureImageGraphQL
  | BaseChemicalStructureImageGraphQL
  | PhosphateChemicalStructureImageGraphQL;

interface ChemicalStructureImageProps {
  imageOrError: ChemicalStructureImageGraphQL;
}

export function ChemicalStructureImage({imageOrError}: ChemicalStructureImageProps): JSX.Element {
  if (imageOrError.__typename === 'ErrorNode') {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={imageOrError.message} />;
  }
  return (
    <img
      className={css(styles.chemicalStructureImage)}
      src={`data:image/svg+xml;base64,${imageOrError.base64Svg}`}
    />
  );
}

export function UnselectedComponentMessage(): JSX.Element {
  return <div className={css(styles.unselectedComponent)}>Select a component to preview structure.</div>;
}

interface MonomerChemicalStructureImageProps {
  nucleotideQueryResult: NucleotideAttachmentQueryResult;
  modificationType: NucleotideModificationType;
}

export function MonomerChemicalStructureImage({
  nucleotideQueryResult,
  modificationType,
}: MonomerChemicalStructureImageProps): JSX.Element {
  if (nucleotideQueryResult.type === QueryResultType.SKIPPED) {
    return <UnselectedComponentMessage />;
  }
  if (nucleotideQueryResult.type === QueryResultType.ERROR) {
    return (
      <InlineBanner bannerType={InlineBannerType.ERROR} text="Error rendering monomer chemical structure" />
    );
  }
  if (nucleotideQueryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }
  assert(
    nucleotideQueryResult.type === QueryResultType.SUCCESS,
    'Expected nucleotide query result to be a success'
  );
  const nucleotideInComplexPolymer = nucleotideQueryResult.data.nucleotideInComplexPolymer;
  assert(
    !isInaccessible(nucleotideInComplexPolymer),
    'Expected complex polymer to be accessible if opened detailed attachment modal'
  );
  switch (modificationType) {
    case NucleotideModificationType.SUGAR:
      return <ChemicalStructureImage imageOrError={nucleotideInComplexPolymer.sugarChemicalStructureImage} />;
    case NucleotideModificationType.BASE:
      return <ChemicalStructureImage imageOrError={nucleotideInComplexPolymer.baseChemicalStructureImage} />;
    case NucleotideModificationType.PHOSPHATE:
      return (
        <ChemicalStructureImage imageOrError={nucleotideInComplexPolymer.phosphateChemicalStructureImage} />
      );
    default:
      return (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="Error rendering monomer chemical structure."
        />
      );
  }
}

interface RGroupOptionsProps {
  attachmentPoints: Array<AttachmentPointGraphQL>;
  prefix: string;
  connectionSideType: ConnectionSideType;
  connection: ComplexPolymerConnection;
  updateConnection: UpdateConnectionFunc;
}

export function RGroupOptions({
  attachmentPoints,
  prefix,
  connectionSideType,
  connection,
  updateConnection,
}: RGroupOptionsProps): JSX.Element {
  if (attachmentPoints.length === 0) {
    return <InlineBanner bannerType={InlineBannerType.WARNING} text="No leaving group configured" />;
  }

  const rGroup =
    connectionSideType === ConnectionSideType.SOURCE ? connection.source.r_group : connection.target.r_group;

  return (
    <div>
      {attachmentPoints.map((attachmentPoint) => (
        <div className={css(styles.rGroup)} key={attachmentPoint.rGroup}>
          <Button
            text={`${prefix}${attachmentPoint.rGroup}`}
            buttonStyle={
              attachmentPoint.rGroup === rGroup ? ButtonStyle.TERTIARY_BLUE : ButtonStyle.TERTIARY_WHITE
            }
            onClick={() => {
              if (rGroup === attachmentPoint.rGroup) {
                updateConnection(connectionSideType, {r_group: null});
              } else {
                updateConnection(connectionSideType, {r_group: attachmentPoint.rGroup});
              }
            }}
            disabled={!attachmentPoint.isAvailable}
            size={ButtonSize.MINI}
          />
        </div>
      ))}
    </div>
  );
}

interface ModificationTypeAndRGroupOptionsProps {
  nucleotideQueryResult: NucleotideAttachmentQueryResult;
  conjugateConnection: ConjugateConnection;
  conjugateConnectionMap: ConjugateConnectionMap;
  isEditMode: boolean;
  forCreate: boolean;
  connection: ComplexPolymerConnection;
  updateConnection: UpdateConnectionFunc;
  connectionSideType: ConnectionSideType;
}

export function ModificationTypeAndRGroupOptions({
  nucleotideQueryResult,
  conjugateConnection,
  conjugateConnectionMap,
  isEditMode,
  forCreate,
  connection,
  updateConnection,
  connectionSideType,
}: ModificationTypeAndRGroupOptionsProps): JSX.Element {
  if (nucleotideQueryResult.type === QueryResultType.ERROR) {
    return <InlineBanner text="Error rendering monomer options." bannerType={InlineBannerType.ERROR} />;
  }
  if (nucleotideQueryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  assert(
    nucleotideQueryResult.type === QueryResultType.SUCCESS,
    'Expected nucleotide query result to be a success'
  );
  const nucleotideInComplexPolymer = nucleotideQueryResult.data.nucleotideInComplexPolymer;
  assert(
    !isInaccessible(nucleotideInComplexPolymer),
    'Expected complex polymer to be accessible if opened detailed attachment modal'
  );

  if (
    nucleotideInComplexPolymer.attachmentPoints.length > 0 &&
    nucleotideInComplexPolymer.attachmentPoints[0]!.__typename === 'ErrorNode'
  ) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text={nucleotideInComplexPolymer.attachmentPoints[0]!.message}
      />
    );
  }
  assert(
    areAttachmentPointNodes(nucleotideInComplexPolymer.attachmentPoints),
    'Expected attachment point node'
  );

  const connectionSide = getConnectionSide(connectionSideType, connection);

  let attachmentPoints = nucleotideInComplexPolymer.attachmentPoints;
  attachmentPoints =
    preprocessAttachmentPointAvailabilityFromConjugateConnections<NucleotideAttachmentPointGraphQL>(
      connectionSide.polymer_ref,
      attachmentPoints,
      conjugateConnectionMap,
      connectionSideType,
      connectionSide.residue_index!
    );

  const modTypeToMonomerName: ModificationTypeToMonomerName = {};
  attachmentPoints.forEach((attachmentPoint) => {
    assert(
      attachmentPoint.modificationType != null && attachmentPoint.monomerName != null,
      'Expected modification type and monomer name to be defined for monomer'
    );
    modTypeToMonomerName[attachmentPoint.modificationType] = attachmentPoint.monomerName;
  });

  const filteredAttachmentPoints = attachmentPoints.filter(
    (attachmentPoint) => attachmentPoint.modificationType === connectionSide.modification_type
  );

  // Changes to attachment details outside the resolved attachment molfile are not allowed for drawn connections
  const disabled =
    !forCreate && isEditMode && connection.attachment_style === ComplexPolymerAttachmentStyle.DRAWN;

  const monomerDropdown = (
    <MonomerSelectDropdown
      modTypeToMonomerName={modTypeToMonomerName}
      connection={connection}
      updateConnection={updateConnection}
      connectionSideType={connectionSideType}
      disabled={disabled}
    />
  );

  return connection.attachment_style === ComplexPolymerAttachmentStyle.DRAWN ? (
    monomerDropdown
  ) : (
    <div className={css(styles.settingsBlock)}>
      <FieldLabel label="Attachment point" />
      {monomerDropdown}
      <RGroupOptions
        attachmentPoints={
          isEditMode
            ? preprocessAttachmentPoints(
                filteredAttachmentPoints,
                conjugateConnection,
                connectionSide.polymer_ref,
                connectionSide.residue_index,
                connectionSideType
              )
            : filteredAttachmentPoints
        }
        prefix="R"
        connectionSideType={connectionSideType}
        connection={connection}
        updateConnection={updateConnection}
      />
    </div>
  );
}

interface MoleculeChemicalStructureImageProps {
  moleculeQueryResult: MoleculeAttachmentQueryResult;
}

export function MoleculeChemicalStructureImage({
  moleculeQueryResult,
}: MoleculeChemicalStructureImageProps): JSX.Element {
  if (moleculeQueryResult.type === QueryResultType.SKIPPED) {
    return <UnselectedComponentMessage />;
  }
  if (moleculeQueryResult.type === QueryResultType.ERROR) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="Error rendering conjugate chemical structure."
      />
    );
  }
  if (moleculeQueryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  assert(
    moleculeQueryResult.type === QueryResultType.SUCCESS,
    'Expected molecule query result to be a success'
  );
  const moleculeInComplexPolymer = moleculeQueryResult.data.moleculeInComplexPolymer;
  assert(
    !isInaccessible(moleculeInComplexPolymer),
    'Expected complex polymer to be accessible if opened detailed attachment modal'
  );
  return <ChemicalStructureImage imageOrError={moleculeInComplexPolymer.chemicalStructureImage} />;
}

interface MoleculeLeavingGroupOptionsProps {
  moleculeQueryResult: MoleculeAttachmentQueryResult;
  polymerRef: string;
  currentConjugateConnection: ConjugateConnection;
  conjugateConnectionMap: ConjugateConnectionMap;
  isEditMode: boolean;
  forCreate: boolean;
  connectionSideType: ConnectionSideType;
  connection: ComplexPolymerConnection;
  updateConnection: UpdateConnectionFunc;
}

export function MoleculeLeavingGroupOptions({
  moleculeQueryResult,
  polymerRef,
  currentConjugateConnection,
  conjugateConnectionMap,
  isEditMode,
  forCreate,
  connectionSideType,
  connection,
  updateConnection,
}: MoleculeLeavingGroupOptionsProps): JSX.Element {
  if (moleculeQueryResult.type === QueryResultType.ERROR) {
    return (
      <InlineBanner bannerType={InlineBannerType.ERROR} text="Error fetching leaving group information." />
    );
  }
  if (moleculeQueryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }
  assert(
    moleculeQueryResult.type === QueryResultType.SUCCESS,
    'Expected molecule query result to be a success'
  );
  const moleculeInComplexPolymer = moleculeQueryResult.data.moleculeInComplexPolymer;
  assert(
    !isInaccessible(moleculeInComplexPolymer),
    'Expected complex polymer to be accessible if opened detailed attachment modal'
  );
  let attachmentPoints = moleculeInComplexPolymer.attachmentPoints;
  if (attachmentPoints.length > 0 && attachmentPoints[0]!.__typename === 'ErrorNode') {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={attachmentPoints[0]!.message} />;
  }
  assert(areAttachmentPointNodes(attachmentPoints), 'Expected all to be AttachmentPointNodes');

  attachmentPoints =
    preprocessAttachmentPointAvailabilityFromConjugateConnections<MoleculeAttachmentPointGraphQL>(
      polymerRef,
      attachmentPoints,
      conjugateConnectionMap,
      connectionSideType
    );
  // The extra assertion is necessary because Typescript is not recognizing that the above function
  // is returning AttachmentPointGraphQL objects only (no null objects)
  assert(areAttachmentPointNodes(attachmentPoints), 'Expected all to be AttachmentPointNodes');

  return (
    <div className={css(styles.settingsBlock)}>
      <FieldLabel label="Leaving group" />
      <RGroupOptions
        attachmentPoints={
          isEditMode
            ? preprocessAttachmentPoints(
                attachmentPoints,
                currentConjugateConnection,
                polymerRef,
                null,
                connectionSideType
              )
            : attachmentPoints
        }
        prefix="LG"
        connectionSideType={connectionSideType}
        connection={connection}
        updateConnection={updateConnection}
      />
    </div>
  );
}

export function getResidueIndexMenuDisplayText(
  complexPolymerType: ComplexPolymerType,
  oligoLength: number,
  oligoPolymerRef: string,
  residueIndex: number
): number {
  if (complexPolymerType === ComplexPolymerType.OLIGO_CONJUGATE) {
    return getDisplayResidueIndex(residueIndex);
  }

  assert(
    complexPolymerType === ComplexPolymerType.OLIGO_DUPLEX,
    'Expected oligo conjugate or oligo duplex entity'
  );
  return getReverseAdjustedDisplayResidueIdx(
    residueIndex,
    oligoLength,
    !isOligoDuplexReverseStrand(oligoPolymerRef)
  );
}

function getResidueIndexMenuOptionDatas(
  complexPolymerType: ComplexPolymerType,
  oligoLength: number,
  oligoPolymerRef: string
): Array<OptionData> {
  const optionDatas = range(0, oligoLength - 1).map((residueIndex) => ({
    name: `${getResidueIndexMenuDisplayText(complexPolymerType, oligoLength, oligoPolymerRef, residueIndex)}`,
    value: residueIndex,
  }));

  if (complexPolymerType === ComplexPolymerType.OLIGO_DUPLEX && isOligoDuplexReverseStrand(oligoPolymerRef)) {
    optionDatas.sort(
      (a: OptionData, b: OptionData) => parseInt(a.name as string, 10) - parseInt(b.name as string, 10)
    );
  }

  return optionDatas;
}

interface MonomerSelectDropdownProps {
  modTypeToMonomerName: ModificationTypeToMonomerName;
  connection: ComplexPolymerConnection;
  updateConnection: UpdateConnectionFunc;
  connectionSideType: ConnectionSideType;
  disabled: boolean;
}

export function MonomerSelectDropdown({
  modTypeToMonomerName,
  connection,
  updateConnection,
  connectionSideType,
  disabled = false,
}: MonomerSelectDropdownProps): JSX.Element {
  const monomerNameMenu = (
    <Menu height={MenuHeight.MEDIUM} width={MenuWidth.FULL}>
      {Object.keys(modTypeToMonomerName).map((modType: NucleotideModificationType) => (
        <Option
          key={modType}
          value={modType}
          onSelect={() => {
            updateConnection(connectionSideType, {r_group: null, modification_type: modType});
          }}
        >
          {modTypeToMonomerName[modType]}
        </Option>
      ))}
    </Menu>
  );
  const connectionSide = getConnectionSide(connectionSideType, connection);
  return (
    <SelectDropdown
      id="monomerMenu"
      key={connectionSideType}
      menu={monomerNameMenu}
      displayName={modTypeToMonomerName[connectionSide.modification_type!]}
      className={css(styles.monomerDropdown)}
      disabled={disabled}
      dataTestAttrs={{
        component: 'MonomerSelectDropdown',
        element: 'root',
      }}
    />
  );
}

interface NucleotideAttachmentSelectionSectionProps extends ComponentRowProps {
  nucleotideQueryResult: NucleotideAttachmentQueryResult | null;
}

function NucleotideAttachmentSelectionSection({
  complexPolymerType,
  isEditMode,
  nucleotideQueryResult,
  simplePolymers,
  currentConjugateConnection,
  conjugateConnectionMap,
  connectionSideType,
  connection,
  updateConnection,
  forCreate,
}: NucleotideAttachmentSelectionSectionProps): JSX.Element | null {
  const selectedPolymerRef = getSelectedPolymerRef(connectionSideType, connection);

  const oligoPolymer = simplePolymers.find(
    (polymer): polymer is NucleotideSimplePolymer =>
      polymer.type === HELMPolymerType.RNA && polymer.helmReference === selectedPolymerRef
  );
  assert(oligoPolymer != null, 'Expected matching polymer for nucleotide sequence');

  const residueIndexMenu = (
    <SearchableMenu
      optionDatas={getResidueIndexMenuOptionDatas(
        complexPolymerType,
        oligoPolymer.bases.length,
        selectedPolymerRef
      )}
      onSelect={(residueIndex: number) => {
        updateConnection(connectionSideType, {
          residue_index: residueIndex,
          modification_type: NucleotideModificationType.SUGAR,
          r_group: null,
        });
      }}
      scrollable={true}
    />
  );

  const connectionSide = getConnectionSide(connectionSideType, connection);
  const attachmentStyle = connection.attachment_style!;

  return (
    <>
      <SelectDropdown
        id="residueIndexMenu"
        className={css(styles.residueIndexMenu)}
        key={connectionSideType}
        menu={residueIndexMenu}
        displayName={
          connectionSide.residue_index != null
            ? `${getResidueIndexMenuDisplayText(
                complexPolymerType,
                oligoPolymer.bases.length,
                connectionSide.polymer_ref,
                connectionSide.residue_index
              )}`
            : 'Select index'
        }
        disabled={
          !forCreate &&
          (isPolymerBackboneStrand(complexPolymerType, selectedPolymerRef) ||
            (isEditMode && attachmentStyle === ComplexPolymerAttachmentStyle.DRAWN))
        }
        dataTestAttrs={{
          component: 'ConfigureComplexPolymerAttachmentsStep',
          element: 'dropdown',
          key: 'residueIndexMenu',
        }}
      />
      {nucleotideQueryResult && connectionSide.residue_index != null && (
        <ModificationTypeAndRGroupOptions
          nucleotideQueryResult={nucleotideQueryResult}
          conjugateConnection={currentConjugateConnection}
          conjugateConnectionMap={conjugateConnectionMap}
          isEditMode={isEditMode}
          connection={connection}
          updateConnection={updateConnection}
          connectionSideType={connectionSideType}
          forCreate={forCreate}
        />
      )}
    </>
  );
}

interface MoleculeAttachmentSelectionSectionProps extends ComponentRowProps {
  moleculeQueryResult: MoleculeAttachmentQueryResult;
}

function MoleculeAttachmentSelectionSection({
  complexPolymerType,
  isEditMode,
  moleculeQueryResult,
  currentConjugateConnection,
  conjugateConnectionMap,
  connectionSideType,
  connection,
  updateConnection,
  forCreate,
}: MoleculeAttachmentSelectionSectionProps): JSX.Element {
  return (
    <>
      {connection.attachment_style === ComplexPolymerAttachmentStyle.LEAVING_GROUP && (
        <MoleculeLeavingGroupOptions
          moleculeQueryResult={moleculeQueryResult}
          polymerRef={getSelectedPolymerRef(connectionSideType, connection)}
          // The current conjugate connection is always identified by the connection target
          currentConjugateConnection={currentConjugateConnection}
          conjugateConnectionMap={conjugateConnectionMap}
          isEditMode={isEditMode}
          forCreate={forCreate}
          connectionSideType={connectionSideType}
          connection={connection}
          updateConnection={updateConnection}
        />
      )}
    </>
  );
}

interface AttachmentSelectionSectionProps extends ComponentRowProps {
  moleculeQueryResult: MoleculeAttachmentQueryResult;
  nucleotideQueryResult: NucleotideAttachmentQueryResult;
}

function AttachmentSelectionSection(props: AttachmentSelectionSectionProps): JSX.Element | null {
  const selectedPolymerRef = getSelectedPolymerRef(props.connectionSideType, props.connection);
  if (selectedPolymerRef === PLACEHOLDER_CONJUGATE_SOURCE_POLYMER_REF) {
    return null;
  }

  if (getPolymerTypeFromRef(selectedPolymerRef) === HELMPolymerType.CHEM) {
    return <MoleculeAttachmentSelectionSection {...omit(props, 'nucleotideQueryResult')} />;
  } else {
    assert(
      getPolymerTypeFromRef(selectedPolymerRef) === HELMPolymerType.RNA,
      'Expected molecule or nucleotide sequence to be selected'
    );
    return <NucleotideAttachmentSelectionSection {...omit(props, 'moleculeQueryResult')} />;
  }
}

interface ComponentSelectionDropdownProps {
  complexPolymerType: ComplexPolymerType;
  conjugateConnectionMap: ConjugateConnectionMap;
  connectionSideType: ConnectionSideType;
  connection: ComplexPolymerConnection;
  updateConnection: UpdateConnectionFunc;
  simplePolymers: Array<SimplePolymer>;
  forCreate: boolean;
}

function ComponentSelectionDropdown({
  complexPolymerType,
  connection,
  updateConnection,
  connectionSideType,
  conjugateConnectionMap,
  simplePolymers,
  forCreate,
}: ComponentSelectionDropdownProps): JSX.Element {
  const selectedPolymerRef = getSelectedPolymerRef(connectionSideType, connection);
  const oppositeSidePolymerRef =
    connectionSideType === ConnectionSideType.SOURCE
      ? connection.target.polymer_ref
      : connection.source.polymer_ref;

  const getMenuOptionFromPolymerRef = (ref: string): JSX.Element => {
    const polymer = getPolymerByRef(ref, simplePolymers);
    return (
      <Option
        key={polymer.helmReference}
        value={polymer.helmReference}
        onSelect={() => {
          updateConnection(connectionSideType, {
            polymer_ref: polymer.helmReference,
            r_group: null,
            residue_index: null,
            modification_type:
              getPolymerTypeFromRef(polymer.helmReference) === HELMPolymerType.RNA
                ? NucleotideModificationType.SUGAR
                : null,
          });
        }}
      >
        {polymer.name}
      </Option>
    );
  };

  const getMenuOptions = (): Array<JSX.Element> => {
    const options: Array<JSX.Element> = [];
    if (complexPolymerType === ComplexPolymerType.OLIGO_CONJUGATE) {
      options.push(<SectionHeader>Backbone</SectionHeader>);
      options.push(<Section>{getMenuOptionFromPolymerRef(OLIGO_CONJUGATE_BACKBONE_STRAND_REF)}</Section>);
    } else if (complexPolymerType === ComplexPolymerType.OLIGO_DUPLEX) {
      options.push(<SectionHeader>Forward strand</SectionHeader>);
      options.push(<Section>{getMenuOptionFromPolymerRef(OLIGO_DUPLEX_FORWARD_STRAND_REF)}</Section>);
      options.push(<SectionHeader>Reverse strand</SectionHeader>);
      options.push(<Section>{getMenuOptionFromPolymerRef(OLIGO_DUPLEX_REVERSE_STRAND_REF)}</Section>);
    }
    const conjugateOptions = Object.values(conjugateConnectionMap)
      .filter((conjugateConnection) => conjugateConnection.helmReference !== oppositeSidePolymerRef)
      .map((conjugateConnection) => getMenuOptionFromPolymerRef(conjugateConnection.helmReference));
    if (conjugateOptions.length > 0) {
      options.push(<SectionHeader>Component</SectionHeader>);
      options.push(<Section>{...conjugateOptions}</Section>);
    }

    return options;
  };

  return (
    <div className={css(styles.componentSelectionDropdown)}>
      <FieldLabel
        label={isPolymerBackboneStrand(complexPolymerType, selectedPolymerRef) ? 'Backbone' : 'Component'}
      />
      <SelectDropdown
        id={`${connectionSideType === ConnectionSideType.SOURCE ? 'source' : 'target'}ComponentMenu`}
        menu={
          <Menu height={MenuHeight.MEDIUM} width={MenuWidth.FULL}>
            {...getMenuOptions()}
          </Menu>
        }
        displayName={getPolymerByRef(selectedPolymerRef, simplePolymers).name}
        disabled={connectionSideType === ConnectionSideType.TARGET || !forCreate}
        dataTestAttrs={{
          component: 'ConfigureComplexPolymerAttachmentsStep',
          element: 'dropdown',
          key: 'componentSelectionDropdown',
        }}
      />
    </div>
  );
}

interface ComponentChemicalStructureImageProps {
  connectionSide: ComplexPolymerBondConnectionSide;
  moleculeQueryResult: MoleculeAttachmentQueryResult;
  nucleotideQueryResult: NucleotideAttachmentQueryResult;
}

function ComponentChemicalStructureImage({
  connectionSide,
  moleculeQueryResult,
  nucleotideQueryResult,
}: ComponentChemicalStructureImageProps): JSX.Element {
  let chemicalStructureImage = null;
  const selectedPolymerRef = connectionSide.polymer_ref;
  if (selectedPolymerRef === PLACEHOLDER_CONJUGATE_SOURCE_POLYMER_REF) {
    return <UnselectedComponentMessage />;
  }
  if (getPolymerTypeFromRef(selectedPolymerRef) === HELMPolymerType.CHEM) {
    chemicalStructureImage = <MoleculeChemicalStructureImage moleculeQueryResult={moleculeQueryResult} />;
  } else {
    assert(
      getPolymerTypeFromRef(selectedPolymerRef) === HELMPolymerType.RNA,
      'Expected molecule or nucleotide component'
    );
    chemicalStructureImage = (
      <MonomerChemicalStructureImage
        nucleotideQueryResult={nucleotideQueryResult}
        modificationType={connectionSide.modification_type!}
      />
    );
  }

  return <div className={css(styles.chemicalStructureImageBlock)}>{chemicalStructureImage}</div>;
}

interface ComponentRowProps {
  complexPolymerId?: string;
  complexPolymerType: ComplexPolymerType;
  isEditMode: boolean;
  simplePolymers: Array<SimplePolymer>;
  currentConjugateConnection: ConjugateConnection;
  connection: ComplexPolymerConnection;
  updateConnection: UpdateConnectionFunc;
  connectionSideType: ConnectionSideType;
  conjugateConnectionMap: ConjugateConnectionMap;
  forCreate: boolean;
  onClickAttach?: (conjugateConnection: ConjugateConnection) => void;
  attachmentMolfile?: string | null;
  attachmentMolfileError?: string | null;
}

export function ComponentRow({
  complexPolymerId,
  complexPolymerType,
  connection,
  updateConnection,
  connectionSideType,
  conjugateConnectionMap,
  simplePolymers,
  isEditMode,
  forCreate,
  onClickAttach,
  currentConjugateConnection,
  attachmentMolfile,
  attachmentMolfileError,
}: ComponentRowProps): JSX.Element | null {
  assert(
    forCreate || complexPolymerId != null,
    'Expected complexPolymerId to exist if not creating complex polymer'
  );
  const connectionSide =
    connectionSideType === ConnectionSideType.SOURCE ? connection.source : connection.target;
  const selectedPolymerRef = connectionSide.polymer_ref;
  const selectedPolymer = getPolymerByRef(connectionSide.polymer_ref, simplePolymers);

  const shouldSkipMoleculeQuery =
    getPolymerTypeFromRef(selectedPolymerRef) !== HELMPolymerType.CHEM ||
    selectedPolymerRef === PLACEHOLDER_CONJUGATE_SOURCE_POLYMER_REF;
  const moleculeQueryResultForCreate = useSkippableBenchlingQuery<
    MoleculeAttachmentForCreateQuery,
    MoleculeAttachmentForCreateQueryVariables
  >(MOLECULE_ATTACHMENT_FOR_CREATE_QUERY, {
    variables: {
      moleculeId: selectedPolymer.id,
      polymerRef: connectionSide.polymer_ref,
    },
    skip: shouldSkipMoleculeQuery || !forCreate,
    fetchPolicy: 'network-only',
  });
  const moleculeQueryResultForEdit = useSkippableBenchlingQuery<
    MoleculeAttachmentModalQuery,
    MoleculeAttachmentModalQueryVariables
  >(MOLECULE_ATTACHMENT_MODAL_QUERY, {
    variables: {
      complexPolymerId: complexPolymerId!,
      polymerRef: connectionSide.polymer_ref,
    },
    skip: shouldSkipMoleculeQuery || forCreate,
    fetchPolicy: 'network-only',
  });

  const shouldSkipNucleotideQuery =
    getPolymerTypeFromRef(selectedPolymerRef) !== HELMPolymerType.RNA ||
    selectedPolymerRef === PLACEHOLDER_CONJUGATE_SOURCE_POLYMER_REF ||
    connectionSide.residue_index == null;
  const nucleotideQueryResultForCreate = useSkippableBenchlingQuery<
    NucleotideAttachmentForCreateQuery,
    NucleotideAttachmentForCreateQueryVariables
  >(NUCLEOTIDE_ATTACHMENT_FOR_CREATE_QUERY, {
    variables: {
      oligoId: selectedPolymer.id,
      polymerRef: connectionSide.polymer_ref,
      residueIndex: connectionSide.residue_index!,
      complexPolymerType: getGQLComplexPolymerType(complexPolymerType),
    },
    skip: shouldSkipNucleotideQuery || !forCreate,
    fetchPolicy: 'network-only',
  });
  const nucleotideQueryResultForEdit = useSkippableBenchlingQuery<
    NucleotideAttachmentModalQuery,
    NucleotideAttachmentModalQueryVariables
  >(NUCLEOTIDE_ATTACHMENT_MODAL_QUERY, {
    variables: {
      complexPolymerId: complexPolymerId!,
      polymerRef: connectionSide.polymer_ref,
      residueIndex: connectionSide.residue_index!,
      complexPolymerType: getGQLComplexPolymerType(complexPolymerType),
    },
    skip: shouldSkipNucleotideQuery || forCreate,
    fetchPolicy: 'network-only',
  });

  const isAttachDisabled = shouldDisableAttachButton(
    connection,
    attachmentMolfile ?? null,
    attachmentMolfileError ?? null
  );

  return (
    <div
      className={css(
        connection.attachment_style === ComplexPolymerAttachmentStyle.DRAWN &&
          connectionSideType === ConnectionSideType.TARGET
          ? styles.targetComponentRowDrawn
          : styles.componentRow
      )}
    >
      <div
        className={css(
          connection.attachment_style === ComplexPolymerAttachmentStyle.DRAWN
            ? styles.attachmentSelectionDrawn
            : styles.attachmentSelection
        )}
      >
        <ComponentSelectionDropdown
          complexPolymerType={complexPolymerType}
          connection={connection}
          updateConnection={updateConnection}
          connectionSideType={connectionSideType}
          conjugateConnectionMap={conjugateConnectionMap}
          simplePolymers={simplePolymers}
          forCreate={forCreate}
        />
        <AttachmentSelectionSection
          complexPolymerType={complexPolymerType}
          isEditMode={isEditMode}
          moleculeQueryResult={forCreate ? moleculeQueryResultForCreate : moleculeQueryResultForEdit}
          nucleotideQueryResult={forCreate ? nucleotideQueryResultForCreate : nucleotideQueryResultForEdit}
          simplePolymers={simplePolymers}
          currentConjugateConnection={currentConjugateConnection}
          conjugateConnectionMap={conjugateConnectionMap}
          connectionSideType={connectionSideType}
          connection={connection}
          updateConnection={updateConnection}
          forCreate={forCreate}
        />
        {connectionSideType === ConnectionSideType.SOURCE && forCreate && onClickAttach && (
          <div className={css(styles.attachButton)}>
            <Tooltip text={attachmentMolfileError}>
              <Button
                buttonStyle={ButtonStyle.TERTIARY_BLUE}
                size={ButtonSize.SMALL}
                disabled={isAttachDisabled}
                text="Attach"
                onClick={() => {
                  trackEvent(MODBIO_ANALYTICS_ACTIONS.ADDED_ATTACHMENT_IN_ATTACHMENT_MODAL, {
                    action_origin: ActionOrigin.MODBIO_COMPLEX_POLYMERS,
                    action_method: ActionMethod.BUTTON_CLICK,
                  });
                  onClickAttach(currentConjugateConnection);
                }}
                dataTestAttrs={{
                  component: 'ConfigureComplexPolymerAttachmentsStep',
                  element: 'button',
                  key: 'attachButton',
                }}
              />
            </Tooltip>
          </div>
        )}
      </div>
      {connection.attachment_style === ComplexPolymerAttachmentStyle.LEAVING_GROUP && (
        <div className={css(styles.chemicalStructureImageWrapper)}>
          <ComponentChemicalStructureImage
            connectionSide={connectionSide}
            moleculeQueryResult={forCreate ? moleculeQueryResultForCreate : moleculeQueryResultForEdit}
            nucleotideQueryResult={forCreate ? nucleotideQueryResultForCreate : nucleotideQueryResultForEdit}
          />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  monomerDropdown: {
    paddingBottom: 5,
  },
  residueIndexMenu: {
    marginBottom: 4,
  },
  attachButton: {
    position: 'absolute',
    bottom: '10px',
    right: '15px',
  },
  chemicalStructureImageBlock: {
    background: GRAY_LIGHTER,
    padding: 10,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: `1px solid ${Color.GRAY_200}`,
    height: '100%',
  },
  chemicalStructureImageWrapper: {
    width: '70%',
  },
  componentSelectionDropdown: {
    marginBottom: 4,
  },
  componentRow: {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    flexBasis: 0,
    minHeight: 0,
  },
  targetComponentRowDrawn: {
    display: 'flex',
    flexDirection: 'row',
  },
  attachmentSelection: {
    padding: '15px',
    position: 'relative',
    width: '30%',
  },
  attachmentSelectionDrawn: {
    padding: '15px',
    position: 'relative',
    width: '100%',
  },
  chemicalStructureImage: {
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  settingsBlock: {
    background: GRAY_LIGHTER,
    padding: 10,
    borderBottomLeftRadius: 5,
    borderBottomRightRadius: 5,
  },
  rGroup: {
    paddingRight: 5,
    paddingTop: 2.5,
    display: 'inline-block',
  },
  unselectedComponent: {
    minHeight: '250px',
    display: 'flex',
    alignItems: 'center',
  },
});
